import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'

import classnames from 'classnames'

import type { ListOptionProps } from '~/data/forms/shared/interfaces/Form'
import useDocumentHeight from '~/hooks/useDocumentHeight'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import { Icon } from '../Icon/Icon'
import ProfileImage, { ProfileImageType } from '../ProfileImage/ProfileImage'
import SpecificImage from '../SpecificImage/SpecificImage'
import Text, { TextSizes } from '../Text/Text'

import styles from './SearchAbleSelect.module.css'

export interface SearchAbleSelectProps {
  name: string
  options: ListOptionProps[]
  type?: ProfileImageType
  testID?: string
  onChange?: any
  onBlurHandler?: (e: React.FocusEvent<HTMLInputElement>) => void
  value?: any
  placeholder?: string
  inputChange?: any
  label?: string
  childEl?: ReactElement
  preMenuTitle?: string
  hideResultsWithNoQuery?: boolean
  onFocus?: (isFocussed: boolean) => void
  reducedHeight?: boolean
  showToggleMenu?: boolean
  searchTerm?: string
  emptyPromptComponent?: ReactElement
  zIndex?: number
  hidePreMenuTitle?: boolean
  smallHeight?: boolean
}

export default function SearchAbleSelect({
  name,
  options,
  type = ProfileImageType.Artist,
  testID,
  onChange,
  onBlurHandler,
  value = '',
  placeholder = 'Type or select an option',
  inputChange,
  label,
  childEl,
  preMenuTitle,
  hideResultsWithNoQuery,
  onFocus,
  searchTerm,
  reducedHeight = false,
  showToggleMenu = true,
  emptyPromptComponent,
  zIndex,
  hidePreMenuTitle = false,
  smallHeight = false,
}: SearchAbleSelectProps) {
  const [isFocussed, setIsFocussed] = useState<boolean>(false)
  const docHeight = useDocumentHeight()
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)
  const [openMenuHeight, setOpenMenuHeight] = useState<number | 'auto'>('auto')

  function findItemByValue(array: any, value: string) {
    return array.find((item: any) => item.value === value)
  }

  const [localValue, setLocalValue] = useState<string | undefined>()
  const [localImage, setLocalImage] = useState<string | undefined>()

  useEffect(() => {
    if (findItemByValue(options, value)?.label) {
      setLocalValue(findItemByValue(options, value)?.label)
    }
    if (findItemByValue(options, value)?.image) {
      setLocalImage(findItemByValue(options, value)?.image)
    }
  }, [value, options])

  const handleInputChange = (val: string) => {
    inputChange?.(val)
  }

  const elRef = useRef<HTMLLabelElement>(null)
  const textInputRef = useRef<HTMLInputElement>(null)

  const handleOnFocus = () => {
    inputChange?.(localValue)
    setIsFocussed(true)
    onFocus?.(true)
  }

  useEffect(() => {
    if (!localValue || localValue.trim() === '') {
      setLocalImage(undefined)
    }
  }, [localValue])

  const clearSelection = () => {
    inputChange(null)
    onChange(name, undefined)
    setLocalValue('')
    setLocalImage(undefined)
  }

  useEffect(() => {
    if (isFocussed) {
      textInputRef.current?.focus()
    }
  }, [isFocussed])

  useEffect(() => {
    if (isMobile && isFocussed) {
      setOpenMenuHeight(docHeight - 170)
    } else {
      setOpenMenuHeight('auto')
    }
  }, [isFocussed, docHeight, isMobile])

  const searchResultLabels = options?.map(item => item.label.toLowerCase())
  const isMatched = searchResultLabels.some(
    label => label.indexOf(localValue?.toLowerCase() || '') !== -1
  )

  return (
    <div
      data-testid={testID}
      style={{
        position: 'relative',
        zIndex: zIndex ? zIndex : undefined,
      }}
    >
      <div
        className={classnames(
          styles.base,
          isFocussed && [styles.isFocussed, 'focussed'],
          reducedHeight && styles.reducedHeight,
          smallHeight && styles.smallHeight,
          {
            [styles.artwork]: type === ProfileImageType.Artwork,
          }
        )}
        style={{
          zIndex: zIndex ? zIndex : undefined,
        }}
      >
        {showToggleMenu && (
          <button
            aria-label={'toggle menu'}
            className={classnames(styles.toggle)}
            onClick={() => {
              setIsFocussed(false)
              onFocus?.(false)
            }}
          >
            <Icon type="Chevron" width={12} />
          </button>
        )}
        {label && (
          <label className={styles.label} ref={elRef}>
            <Text size={TextSizes.bodySml}>{label}</Text>
          </label>
        )}
        <div className={styles.comboBox}>
          <div className={styles.searchInput}>
            {!localImage ? (
              <Icon type={'Magnify'} width={16} />
            ) : (
              <div className={styles.imageWrapper}>
                <SpecificImage
                  asset={localImage}
                  width={24}
                  className={styles.image}
                  alt={name}
                />
              </div>
            )}
            <input
              value={localValue}
              name={name}
              onChange={e => {
                setLocalValue(e.currentTarget.value)

                handleInputChange(e.currentTarget.value)
              }}
              onFocus={handleOnFocus}
              onBlur={onBlurHandler}
              className={styles.input}
              data-testid={testID && `select-${testID}`}
              ref={textInputRef}
              placeholder={placeholder}
              autoComplete="off"
            />
          </div>
          <div className={styles.clearButton}>
            {localValue && (
              <div
                onClick={() => clearSelection()}
                className={styles.clearIcon}
                aria-label="clear selection"
              >
                <Icon type="CrossFilled" />
              </div>
            )}
          </div>
        </div>

        <div className={styles.menuWrapper}>
          {options &&
            (options.length > 0 || emptyPromptComponent) &&
            !(hideResultsWithNoQuery && !localValue) && (
              <>
                <ul
                  className={classnames(
                    styles.menu,
                    isFocussed && styles.menuOpen,
                    smallHeight && styles.smallHeight,
                    {
                      [styles.artworkMenu]: type === ProfileImageType.Artwork,
                      [styles.noEmptyResultsContainer]: !emptyPromptComponent,
                    }
                  )}
                  style={{ maxHeight: openMenuHeight }}
                  data-testid={testID && `options-${testID}`}
                >
                  {!hidePreMenuTitle &&
                    preMenuTitle &&
                    isFocussed &&
                    !localValue && (
                      <li className={styles.pretitle}>
                        <Text size={TextSizes.secondaryTitleMedReg} italic>
                          {preMenuTitle}
                        </Text>
                      </li>
                    )}
                  {isFocussed ? (
                    <>
                      {options.map((item: any) => (
                        <li
                          key={`${item.value}-${item.label}`}
                          className={classnames(styles.menuItem, {
                            [styles.artwork]: type === ProfileImageType.Artwork,
                          })}
                          data-testid={testID && `option-${testID}`}
                          onClick={() => {
                            setLocalValue(item.label)
                            setLocalImage(item.image)
                            onChange?.(name, item.value)
                            setIsFocussed(false)
                          }}
                        >
                          <ProfileImage
                            name={item.label}
                            image={item.image}
                            altName={item?.meta?.secondTitle || ''}
                            format={item?.meta?.thirdTitle || ''}
                            _id={item.value}
                            inline={true}
                            smallImage={true}
                            stackText={type === ProfileImageType.Artwork}
                            type={type}
                            fallBackToInitials
                            highlightText={searchTerm}
                          />
                        </li>
                      ))}
                      {!isMatched && (
                        <li
                          className={styles.childEl}
                          onClick={e => e.stopPropagation()}
                        >
                          {childEl}
                        </li>
                      )}
                    </>
                  ) : null}
                  {isFocussed &&
                    options.length === 0 &&
                    emptyPromptComponent &&
                    emptyPromptComponent}
                </ul>
              </>
            )}
        </div>
        {localValue && options?.length === 0 && (
          <div className={styles.childEl} onClick={e => e.stopPropagation()}>
            {childEl}
          </div>
        )}
      </div>
    </div>
  )
}
